import { FC, useState } from 'react';
import { useMutation } from '@apollo/client';

import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import useToast from 'components/ToastProvider/useToast';
import { UpdateCompanyProfileTaxDetailsDocument } from 'features/Accounting/pages/Estimation/components/EstimationSettingsContainer/components/EstimationSettings/hooks/updateCompanyProfileTaxDetails/updateCompanyProfileTaxDetails.gql';
import { UpdateTaxDataDocument } from 'features/Accounting/pages/Estimation/graphql/updateTaxData.gql';
import {
  CurrentTaxForecastPeriod,
  TaxForecastProfile,
} from 'features/Accounting/pages/Estimation/hooks/useTaxForecastData';

import StepLayout from '../../layout/Step';
import { StepComponentProps } from '../types';
import { locales } from './locales';

type SavePayload = {
  taxFrequency: string;
  taxForecastPeriod: CurrentTaxForecastPeriod;
  taxForecastProfile: TaxForecastProfile;
};

const TaxFrequency: FC<StepComponentProps<SavePayload>> = ({
  context,
  onBack,
  onNext,
  onSave,
}) => {
  const { showToast } = useToast();
  const { companyProfile, stepProgress } = context;
  const [selectedValue, setSelectedValue] = useState(
    companyProfile.taxFrequency,
  );

  const [updateTaxData, { loading: updateTaxDataLoading }] = useMutation(
    UpdateTaxDataDocument,
    {
      variables: {
        input: {
          companyProfileId: companyProfile.companyProfileId,
          taxFrequency: selectedValue,
        },
      },
    },
  );

  const [updateCompanyProfile, { loading: updateCompanyProfileLoading }] =
    useMutation(UpdateCompanyProfileTaxDetailsDocument, {
      variables: {
        input: {
          companyProfileId: companyProfile.companyProfileId,
          taxFrequency: selectedValue,
        },
      },
    });

  const handleSave = async () => {
    /**
     * We must update the tax data first to:
     * - ensure that the forecast profile and period are created
     * - ensure that the profile and period have the customized estimation flag
     */
    const updateTaxDataResult = await updateTaxData();
    const updateCompanyProfileResult = await updateCompanyProfile();

    onSave({
      taxForecastPeriod: updateTaxDataResult.data?.updateTaxData
        .currentTaxForecastPeriod as CurrentTaxForecastPeriod,
      taxForecastProfile: updateTaxDataResult.data?.updateTaxData
        .taxForecastProfile as TaxForecastProfile,
      taxFrequency: updateCompanyProfileResult.data?.updateCompanyProfile
        .company?.taxFrequency as string,
    });
  };

  const handleNext = async () => {
    try {
      await handleSave();
      onNext();
    } catch {
      showToast({ message: locales.error, type: 'error' });
    }
  };

  return (
    <StepLayout>
      <StepLayout.Header onBack={onBack} stepProgress={stepProgress} />

      <StepLayout.Body>
        <Heading marginBottom="space-32">{locales.title}</Heading>

        <SunshineCard.RadioGroup
          onChange={(value) => setSelectedValue(value)}
          value={selectedValue}
        >
          <SunshineCard
            title={locales.monthly}
            value="MONTHLY"
            variant="radio"
          />

          <SunshineCard
            title={locales.quarterly}
            value="QUARTERLY"
            variant="radio"
          />
        </SunshineCard.RadioGroup>
      </StepLayout.Body>

      <StepLayout.Footer stepProgress={stepProgress}>
        <StepLayout.FooterButton
          isDisabled={
            selectedValue !== 'MONTHLY' && selectedValue !== 'QUARTERLY'
          }
          isLoading={updateCompanyProfileLoading || updateTaxDataLoading}
          onClick={handleNext}
        />
      </StepLayout.Footer>
    </StepLayout>
  );
};

export default TaxFrequency;
